import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Drawer } from "@/config/antd";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTicketsByAny,
  selectFetchedTickets,
} from "@/store/redux/slices/support/support_slice";
import { getOrganizationId } from "@/utils/token_functions";
import SupportTicketList from "@/components/layout/Header/support/SupportTicketList";
import SupportChatView from "@/components/layout/Header/support/SupportChatView";

const SupportChatDrawer = ({ open, onClose, navigate }) => {
  const dispatch = useDispatch();
  const supportTickets = useSelector(selectFetchedTickets);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [drawerWidth, setDrawerWidth] = useState("450px");

  useEffect(() => {
    const handleResize = () => {
      setDrawerWidth(window.innerWidth < 640 ? "100%" : "450px");
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadTickets = useCallback(async () => {
    const organizationId = getOrganizationId();
    if (!organizationId) return;

    setLoading(true);
    try {
      await dispatch(
        fetchTicketsByAny({
          key: "organizationID",
          value: organizationId,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error fetching support tickets:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (open) {
      loadTickets();
    }
  }, [open, loadTickets]);

  const handleDrawerClose = () => {
    setSelectedTicketId(null);
    setSearchQuery("");
    onClose();
  };

  const handleSeeAll = () => {
    handleDrawerClose();
    navigate("/dashboard/settings/support");
  };

  return (
    <Drawer
      title={null}
      placement="right"
      onClose={handleDrawerClose}
      open={open}
      width={drawerWidth}
      className="chat-drawer"
      mask={true}
      maskClosable={true}
      closable={false}
      styles={{
        mask: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
        body: { padding: "24px" },
      }}>
      {!selectedTicketId ? (
        <SupportTicketList
          tickets={supportTickets}
          loading={loading}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onTicketSelect={setSelectedTicketId}
          onSeeAll={handleSeeAll}
        />
      ) : (
        <SupportChatView
          ticketId={selectedTicketId}
          onBack={() => setSelectedTicketId(null)}
          onTicketClosed={loadTickets}
        />
      )}
    </Drawer>
  );
};

SupportChatDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default SupportChatDrawer;
